import { Component, OnInit, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { Validators, ValidationErrors } from '@angular/forms';
import { AbstractControl } from '@angular/forms';

import { Subscription } from 'rxjs';

import { ViewRenderer } from '@unpispas/upp-wdgt';

import { eventbusService } from '@unpispas/upp-base';
import { alertService } from '@unpispas/upp-base';
import { toastService } from '@unpispas/upp-base';
import { languageService, languageSupport } from '@unpispas/upp-base';
import { dataService } from '@unpispas/upp-data';
import { viewService } from '@unpispas/upp-base';
import { loginService } from '@unpispas/upp-data';

import { UserView } from '@unpispas/upp-data';

import { EVTBUS_EDIT_USER } from '@unpispas/upp-base';

@Component({
    selector: 'upp-user-edit',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-edit.html',
    styleUrls: [
      './upp-edit.scss'
    ],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef]
    }]       
})
export class UppUserEditComponent extends languageSupport implements OnInit, OnDestroy {
    private _activate_subscription: Subscription | null = null;
    private _user_subscription: Subscription | null = null;

    get user(): UserView | null {
        return this.data.alives.get(this.data.user) as UserView;
    }    

    constructor(public lang: languageService, private change: ViewRenderer, private evtbus: eventbusService, private alertCtrl: alertService, private toast: toastService, private login: loginService, private data: dataService, public view: viewService){
        super(lang, change.cdref);
    }

    private _isactive = false;
    private _ishidden = true;

    get IsHidden(){
        return this._ishidden;
    }

    get IsActive(){
        return this._isactive;
    }

    set IsActive(value: boolean){
        this._isactive = true;
        this._ishidden = true;
        this.change.markForCheck();
    
        if (value){
            setTimeout(() => {
                this._ishidden = false;
                this.change.markForCheck();
            }, 100);
        }
        else {
            setTimeout(() => {
                this._isactive = false;
                this.change.markForCheck();
            }, 500);
        }
    }

    ngOnInit(): void {
        this._activate_subscription = this.evtbus.on<UserView | null>(EVTBUS_EDIT_USER).subscribe(
        () => {
            this.IsActive = true;

            if (this._user_subscription) {
                this._user_subscription.unsubscribe();
                this._user_subscription = null;
            }
    
            if (this.user) {
                this._user_subscription = this.user.OnRefresh.subscribe(
                () => {
                    this.change.markForCheck();
                });
            }
        });
    }

    ngOnDestroy(): void {
        super.OnDestroy();

        if (this._activate_subscription){
            this._activate_subscription.unsubscribe();
            this._activate_subscription = null;
        }

        if (this._user_subscription) {
            this._user_subscription.unsubscribe();
            this._user_subscription = null;
        }
    }
    
    /********************************/
    /* USERINFO FORM                */
    /********************************/

    private checkAvailableUser(control: FormControl): ValidationErrors | null {
        const _username = control.value;
        if (_username && (_username != this.user?.email)){
            this.login.Available(_username).then(
            data => {
                if (!data && control.valid){
                    control.setErrors({ available: true });    
                }
            });
        }

        return null;
    }
    
    private _userinfoForm: FormGroup | null = null;   
    private LoadUserForm(): FormGroup {
        const _photo: string | null = this.user?.photo || null;
        const _language: string = this.lang.GetLanguage(true);
        const _firstname: string | null = this.user?.firstname || null;
        const _lastname: string | null = this.user?.lastname || null;
        const _phone: string | null = this.user?.phone || null;
        const _email: string | null = this.user?.email || null;

        let _form: FormGroup | null = this._userinfoForm;
        if (_form == null){
            _form = new FormGroup({
                photo: new FormControl(_photo, [
                    // no validators required
                ]),
                language: new FormControl(_language, [
                    // no validators required
                ]),
                firstname: new FormControl(_firstname, [
                    // no validators required
                ]),
                lastname: new FormControl(_lastname, [
                    // no validators required
                ]),
                phone: new FormControl(_phone, [
                    // no validators required
                ]),
                email: new FormControl(_email, [
                    Validators.required,
                    Validators.email,
                    this.checkAvailableUser.bind(this)
                ]),
            });
        }
        else {
            _form.patchValue({
                photo: _photo,
                language: _language,
                firstname: _firstname,
                lastname: _lastname,
                phone: _phone,
                email: _email
            });
        }
        
        this.change.markForCheck();
        
        _form.markAsPristine();
        setTimeout(() => {
            _form?.markAsPristine();
        }, 0)    
        
        return _form        
    }

    get UserForm(): FormGroup {
        if (this._userinfoForm == null){
            this._userinfoForm = this.LoadUserForm();
        }

        const _language = this.lang.GetLanguage(true);
        const _control = this._userinfoForm.get('language');
        if (_control && (_control.value !== _language)){
            this._userinfoForm.patchValue({ language: _language }, { emitEvent: false });
        }

        return this._userinfoForm;        
    }  

    /********************************/
    /* PASSWORD FORM                */
    /********************************/

    private checkConfirmPassword(group: AbstractControl): ValidationErrors | null {
        const _previous = group.get('previous');
        const _newpass = group.get('newpass');
        const _confirm = group.get('confirm');
        
        if (_previous && _newpass){
            const _samePassword = (_previous.value !== null) && (_newpass.value !== null) && (_previous.value === _newpass.value);
            if (_samePassword){
                _newpass.setErrors({ ...(_newpass.errors || {}), samepwd: true });
            }
            else if (_newpass.errors?.['samepwd']){
                const _errors = { ..._newpass.errors };
                delete _errors['samepwd'];
                _newpass.setErrors(Object.keys(_errors).length ? _errors : null);
            }
        }

        if (_newpass && _confirm){
            if (_newpass.value !== _confirm.value){
                _confirm.setErrors({ ...(_confirm.errors || {}), nomatch: true });
            }
            else if (_confirm.errors?.['nomatch']){
                const _errors = { ..._confirm.errors };
                delete _errors['nomatch'];
                _confirm.setErrors(Object.keys(_errors).length ? _errors : null);
            }
        }
        
        return null;  
    }

    private _passwordForm: FormGroup | null = null;   
    private LoadPasswordForm(): FormGroup {
        const _previous: string | null = null;
        const _newpass: string | null = null;
        const _confirm: string | null = null;

        let _form: FormGroup | null = this._passwordForm;
        if (_form == null){
            _form = new FormGroup({
                previous: new FormControl(_previous, [
                    Validators.required,
                    Validators.minLength(8)                        
                ]),
                newpass: new FormControl(_newpass, [
                    Validators.required,
                    Validators.minLength(8)                        
                ]),
                confirm: new FormControl(_confirm, [
                    Validators.required,
                    Validators.minLength(8)                        
                ])
            }, { validators: this.checkConfirmPassword.bind(this) });
        }
        else {
            _form.patchValue({
                previous: _previous,
                newpass: _newpass,
                confirm: _confirm
            });
        }
        
        this.change.markForCheck();
        
        _form.markAsPristine();
        setTimeout(() => {
            _form?.markAsPristine();
        }, 0)    
        
        return _form        
    }

    get PassForm(): FormGroup {
        if (this._passwordForm == null){
            this._passwordForm = this.LoadPasswordForm();
        }
        return this._passwordForm;        
    }

    /*******************************/
    /* DISPLAY OPTIONS             */
    /*******************************/
    
    private _checkingavailable = false;
    private get CheckingAvailable(): boolean {
        return this._checkingavailable;
    }

    private set CheckingAvailable(value: boolean) {
        this._checkingavailable = value;
        this.change.markForCheck();
    }

    get CanAcceptChanges(){
        return this.UserForm.valid && this.UserForm.dirty && !this.CheckingAvailable;
    }

    get CanAcceptPassword(){
        return this.PassForm.valid && this.PassForm.dirty && !this.InvalidPassword && !this.IsChangingPassword;
    }

    private _invalid = false;
    get InvalidPassword(){
        return this._invalid;
    }

    set InvalidPassword(value: boolean){
        this._invalid = value;
        this.change.markForCheck();
    }

    private _ischangingpassword = false;
    get IsChangingPassword(): boolean {
        return this._ischangingpassword;
    }

    set IsChangingPassword(value: boolean){
        this._ischangingpassword = value;
        this.change.markForCheck();
    }
    
    private _resendingActivation = false;

    private _mode: 'USER' | 'PASS' = 'USER';
    get Mode(){
        return this._mode;
    }

    set Mode(value: 'USER' | 'PASS'){
        this._mode = value;
        this.change.markForCheck();
    }

    /*******************************/
    /* EVENT HANDLERS              */
    /*******************************/   
    
    OnLanguageApply(language: any){
        const _language = String(language || '').toUpperCase();
        if (_language && (_language != this.lang.GetLanguage(true))){
            this.lang.SetLanguage(_language);
        }
    }

    OnPasswordChanged(){
        this.InvalidPassword = false;
    }   

    OnUpdatePasword(){
        this.InvalidPassword = false;
        this.LoadPasswordForm();
        this.Mode = 'PASS';
    }

    async AcceptPassword(){
        const _oldpassword: string | null = this.PassForm.get('previous')?.value || null;
        const _newpassword: string | null = this.PassForm.get('newpass')?.value || null;

        if (_oldpassword && _newpassword){
            let _success = false;
   
            this.PassForm.markAsPristine()
            this.IsChangingPassword = true;
            try {
                _success = await this.login.DoPassword(_oldpassword, _newpassword, false);  // try double-CRC
                if (!_success){
                    _success = await this.login.DoPassword(_oldpassword, _newpassword, true);   // try single-CRC
                }
                
                if (_success){
                    this.toast.ShowAlert('success', this.lang.tr('@user_password_changed_ok'));
                    this.CancelPassword();
                }
                else {
                    this.InvalidPassword = true;
                }
            }
            finally {
                this.IsChangingPassword = false;
            }
        }
    }

    CancelPassword(){
        this.InvalidPassword = false;
        this.IsChangingPassword = false;
        this.LoadPasswordForm();
        this.Mode = 'USER';
    }

    OnClose(){
        if (this._user_subscription) {
            this._user_subscription.unsubscribe();
            this._user_subscription = null;
        }
        
        this.InvalidPassword = false;
        this.IsChangingPassword = false;
        this.LoadPasswordForm();
        this.Mode = 'USER';
        this.IsActive = false;
    }

    /**
     * Resends the account activation email (USER status PA) using login/resend.
     */
    async OnResendActivationMail(): Promise<void> {
        const _email = this.user?.email || null;
        if (!_email || this._resendingActivation) {
            return;
        }

        this._resendingActivation = true;
        this.change.markForCheck();

        try {
            const _ok = await this.login.DoActivate(_email);
            if (_ok) {
                const alert = this.alertCtrl.alert({
                    header: this.lang.tr('@user_activation_mail_sent_title'),
                    message: this.lang.tr('@user_activation_mail_sent_message', [ _email ]),
                    backdropDismiss: true,
                    buttons: [{
                        text: this.lang.tr('@ok'),
                        handler: () => {
                            setTimeout(() => {
                                alert.dismiss(true);
                            }, 100);

                            return false;
                        }
                    }]
                });

                await alert.present();
            }
            else {
                this.toast.ShowAlert('danger', this.lang.tr('@register_mail_error'));
            }
        }
        catch {
            this.toast.ShowAlert('danger', this.lang.tr('@register_mail_error'));
        }
        finally {
            this._resendingActivation = false;
            this.change.markForCheck();
        }
    }

    private async OnMailChanged(newmail: string){
        return new Promise((resolve) => {
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@user_data_email_changed_title'),
                message: this.lang.tr('@user_data_email_changed_message', [ newmail ]),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@ok'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
    
                        return false;
                    }
                }]
            });
    
            alert.onDidDismiss().then(
            () => {
                resolve(true);
            });

            alert.present();            
        })
    }

    AcceptChanges(){
        if (!this.user){
            return;
        }

        this.UserForm.markAsPristine()

        // main user information
        const _photo = this.UserForm.get('photo')?.value || null;
        const _language = this.UserForm.get('language')?.value || null;
        const _firstname = this.UserForm.get('firstname')?.value || null;
        const _lastname = this.UserForm.get('lastname')?.value || null;
        const _phone = this.UserForm.get('phone')?.value || null; 
        const _email = this.UserForm.get('email')?.value?.toLowerCase() || null; 

        this.user.photo = _photo;
        this.user.lang = _language;
        this.user.firstname = _firstname;
        this.user.lastname = _lastname;
        this.user.phone = _phone;
        
        // commit the changes
        this.user.DoCommit().then(
        async () => {
            if (this.user && (this.user.email != _email)){
                await this.OnMailChanged(_email);
            }

            this.OnClose();
        });
    }

    CancelChanges(){
        this.LoadUserForm();
        this.LoadPasswordForm();

        this.OnClose();
    }
}
